import List from '@/components/common/List';
import { useMetadataBlock } from '@/hooks/useMetadataBlock';
import { EventArgOption } from '@/types/metadata/metadata';
import { Arg, Subroutine } from '@/types/subroutine';
import { findEventArgPreset } from '@/util/arguments';
import { getHighestNumber } from '@/util/number';
import { isNumber } from '@/util/typeGuards';
import { validateMemberType } from '@/util/validation/subroutines';
import { Card, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import ArgView from './ArgView';

type EventArgumentsProps = {
  id: number;
  subroutine?: Subroutine;
  parentSubroutineId: number;
  updateApp: () => void;
};

const EventArguments: FC<EventArgumentsProps> = ({ id, parentSubroutineId, updateApp, subroutine }) => {
  const { metadataBlock } = useMetadataBlock();

  const [eventIndex, setEventIndex] = useState<number>();

  useEffect(() => {
    const presets = metadataBlock?.event_args;
    const eventArgs = subroutine?.event_args;
    if (!presets || !eventArgs) return;

    const preset = findEventArgPreset(
      presets,
      eventArgs.map((eventArg) => eventArg.label)
    );
    if (!preset) return;

    setEventIndex(presets.indexOf(preset));
  }, [subroutine?.event_args, metadataBlock]);

  if (!metadataBlock || !metadataBlock.event_args) return <></>; //don't show

  const presetArgs = isNumber(eventIndex) ? metadataBlock.event_args[eventIndex]?.args : undefined;

  // The metadata preset owns the label and the type of an event arg, so only the binding is the user's.
  const eventArgFrom =
    (highestId: number) =>
    (presetArg: EventArgOption, index: number, eventArg?: Arg): Arg => ({
      id: eventArg?.id ?? highestId + index + 1,
      label: eventArg?.label || presetArg.label,
      type: presetArg.type
    });

  // Ids are unique within the event args, so a new one continues past those already there.
  const toEventArg = eventArgFrom(getHighestNumber(subroutine?.event_args ?? [], 'id'));
  // Picking a preset replaces the whole list, so its ids start over at 1.
  const toPresetEventArg = eventArgFrom(0);

  // Rebuilds both lists from the preset, so they stay aligned with it and with each other.
  const updateEventArg = (index: number, newArg: Arg, newBinding: string) => {
    if (!subroutine || !presetArgs) return;

    const eventArgs = subroutine.event_args ?? [];
    const bindings = subroutine.event_arg_bindings ?? [];
    subroutine.event_args = presetArgs.map((presetArg, argIndex) =>
      argIndex === index ? newArg : toEventArg(presetArg, argIndex, eventArgs[argIndex])
    );
    subroutine.event_arg_bindings = presetArgs.map((_, argIndex) =>
      argIndex === index ? newBinding : (bindings[argIndex] ?? '')
    );
    updateApp();
  };

  return (
    <Card variant='outlined' sx={{ p: 1 }}>
      <FormControl fullWidth>
        <List
          // subheader="Event Arguments"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'start'
          }}
        >
          <InputLabel id='event-args-label'>Event Arguments</InputLabel>
          <Select
            label='Event Arguments'
            labelId='event-args-label'
            value={isNumber(eventIndex) ? eventIndex : ''}
            size='small'
            fullWidth
            onChange={(e) => {
              const value = e.target.value;
              if (!subroutine || !isNumber(value)) return;
              if (!metadataBlock.event_args || !metadataBlock.event_args[value]) return;

              subroutine.event_args = metadataBlock.event_args[value].args.map((presetArg, index) =>
                toPresetEventArg(presetArg, index)
              );
              subroutine.event_arg_bindings = metadataBlock.event_args[value].args.map(() => '');
              updateApp();
            }}
          >
            {metadataBlock.event_args.map((eventArg, index) => {
              return (
                <MenuItem key={eventArg.label + index} value={index}>
                  {eventArg.label}
                </MenuItem>
              );
            })}
          </Select>
          {presetArgs?.map((presetArg, index) => {
            const eventArg = toEventArg(presetArg, index, subroutine?.event_args?.[index]);
            return (
              <ArgView
                key={presetArg.label + index}
                id={id}
                arg={eventArg}
                binding={subroutine?.event_arg_bindings?.[index] ?? ''}
                typeErrors={subroutine ? validateMemberType(subroutine, 'event argument', eventArg, index) : []}
                type={presetArg.type}
                subroutineId={parentSubroutineId}
                onChange={(newArg, newBinding) => updateEventArg(index, newArg, newBinding)}
                disableFields
              />
            );
          })}
        </List>
      </FormControl>
    </Card>
  );
};

export default EventArguments;
